import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#050505] grid md:grid-cols-2">
      <aside className="hidden md:block relative overflow-hidden bg-[#0a0a0a]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1551958219-acbc608c6377?w=1600&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="relative z-10 h-full p-14 flex flex-col justify-between">
          <Link to="/" className="text-white/60 hover:text-white inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <div>
            <div className="font-display text-7xl uppercase leading-[0.9]">
              Complete<br />Sports<br />
              <span className="text-[#00E5FF]">Group.</span>
            </div>
            <p className="font-body text-white/50 mt-6 max-w-sm">
              Internal CMS. Manage departments, athletes, services and inquiries.
            </p>
          </div>
        </div>
      </aside>

      <main className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="admin-login-form">
          <div className="text-xs tracking-[0.3em] uppercase text-white/40 mb-3">Admin Access</div>
          <h1 className="font-display text-5xl uppercase mb-10">Sign In</h1>

          <div className="space-y-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email"
                className="w-full bg-transparent border border-white/15 focus:border-white outline-none p-4 font-body text-white"
                placeholder="admin@completesportsgroup.com"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
                className="w-full bg-transparent border border-white/15 focus:border-white outline-none p-4 font-body text-white"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 mt-3 px-7 py-4 bg-white text-black font-body text-sm tracking-[0.25em] uppercase hover:bg-white/90 disabled:opacity-50 transition-all"
            >
              {loading ? "Signing in…" : "Sign In"} <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
